/*
 * "Insert video…" in the editor toolbar: ask for a link, validate it, insert a
 * token.
 *
 * The validation is the point. A URI the embed allowlist does not recognise
 * still renders - as an ordinary link - so inserting it unchecked would "work"
 * and the author would only discover the video is not embedded by scrolling to
 * the preview, or worse, not at all. Refusing here, with the reason and the
 * list of what IS supported, puts the discovery at the moment of the mistake.
 *
 * It validates as you type rather than on submit, so pasting a link tells you
 * immediately what it resolved to - "YouTube video" under the box is a much
 * more useful confirmation than silence.
 *
 * The check is the SAME function the renderer uses (parseVideoReference), not a
 * second opinion about what a video URL looks like. A dialog that accepted
 * something the renderer then refused would be worse than no dialog.
 */

import { useEffect, useState } from 'react';
import { buildVideoToken, parseVideoReference } from '@accotool-kb/shared';
import { Modal } from '../../components/ui.js';
import { useMessages } from '../../i18n/language.js';

export function VideoInsertDialog({
  open,
  onClose,
  onInsert,
}: {
  open: boolean;
  onClose: () => void;
  onInsert: (token: string) => void;
}): JSX.Element | null {
  // Its words, the provider names among them: t.admin.editor.videoDialog (S48).
  const words = useMessages().admin.editor.videoDialog;
  const [uri, setUri] = useState('');

  // A fresh dialog every time it opens: the previous link is never what you
  // want next, and a stale error message is confusing.
  useEffect(() => {
    if (open) setUri('');
  }, [open]);

  const trimmed = uri.trim();
  const embed = trimmed ? parseVideoReference(trimmed) : null;
  const canInsert = embed !== null;

  const insert = () => {
    if (!embed) return;
    onInsert(buildVideoToken(trimmed));
    onClose();
  };

  return (
    <Modal
      title={words.title}
      open={open}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button button-quiet" onClick={onClose}>
            {words.cancel}
          </button>
          <button
            type="button"
            className="button button-primary"
            disabled={!canInsert}
            onClick={insert}
          >
            {words.insert}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="video-uri">{words.linkLabel}</label>
        <input
          id="video-uri"
          type="url"
          value={uri}
          /*
            data-autofocus, not autoFocus: React's prop focuses imperatively
            while this dialog is still display:none, so it does nothing at all.
            Modal focuses this element after showModal instead.
          */
          data-autofocus
          placeholder="https://www.youtube.com/watch?v=…"
          aria-describedby="video-uri-status"
          onChange={(event) => setUri(event.target.value)}
          onKeyDown={(event) => {
            // Enter inserts when the link is good, so pasting and pressing
            // Enter is the whole interaction.
            if (event.key === 'Enter' && canInsert) {
              event.preventDefault();
              insert();
            }
          }}
        />
      </div>

      {/*
        One status line, always present so the dialog does not jump as it
        changes, and aria-live so a screen reader hears the verdict rather than
        only seeing a disabled button.
      */}
      <p id="video-uri-status" className={embed ? 'notice' : 'faint'} aria-live="polite">
        {!trimmed ? words.prompt : embed ? words.recognised(embed.provider) : words.notEmbeddable}
      </p>
    </Modal>
  );
}
